import {
	ChevronLeft,
	ChevronRight,
	Eye,
	Filter,
	Plus,
	Search,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import {
	Button,
	CopyButton,
	Notification,
	Table,
	Tabs,
	useNotification,
} from "../../components/FormComponents";
import StatusBadge from "../../components/StatusBadge";
import { useAuthStore } from "../../store/authStore";
import { formatCurrency, formatDate } from "../../utils/format";

const STATUS_OPTIONS = [
	{ value: "", label: "Todos os status" },
	{ value: "PENDING", label: "Pendente" },
	{ value: "ACCEPTED", label: "Aceita" },
	{ value: "REJECTED", label: "Rejeitada" },
];

const LIMIT = 20;

export default function Proposals() {
	const navigate = useNavigate();
	const canCreate = useAuthStore((s) => s.canCreate);
	const [proposals, setProposals] = useState([]);
	const [loading, setLoading] = useState(true);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [activeTab, setActiveTab] = useState("all");
	const { notification, showNotification } = useNotification();

	const fetchProposals = useCallback(async () => {
		try {
			setLoading(true);
			const status = activeTab !== "all" ? activeTab : statusFilter;
			const response = await client.get("/proposals", {
				params: {
					page,
					limit: LIMIT,
					search,
					status,
				},
			});
			const result = response.data;
			setProposals(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Error fetching proposals:", error);
			showNotification("Erro ao carregar propostas", "error");
			setProposals([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, statusFilter, activeTab, showNotification]);

	useEffect(() => {
		fetchProposals();
	}, [fetchProposals]);

	// Reset to page 1 when filters change
	useEffect(() => {
		setPage(1);
	}, []);

	const handleSearch = (e) => {
		if (e) e.preventDefault();
		setSearch(searchInput);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			setSearch(searchInput);
		}
	};

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		setStatusFilter("");
	};

	const handleStatusFilterChange = (value) => {
		setStatusFilter(value);
		setActiveTab("all");
	};

	const totalPages = Math.max(1, Math.ceil(total / LIMIT));
	const startItem = total === 0 ? 0 : (page - 1) * LIMIT + 1;
	const endItem = Math.min(page * LIMIT, total);

	const tabs = [
		{ value: "all", label: "Todas" },
		{ value: "PENDING", label: "Pendentes" },
		{ value: "ACCEPTED", label: "Aceitas" },
		{ value: "REJECTED", label: "Rejeitadas" },
	];

	const getProposalLink = (p) => {
		const token = p.access_token;
		if (token) {
			return `${window.location.origin}/p/${token}`;
		}
		return null;
	};

	const columns = [
		{
			key: "proposal_number",
			label: "Proposta",
			render: (val, row) => (
				<div>
					<span className="font-mono text-cyan-300 font-bold">{val}</span>
					{row.title && (
						<p className="text-xs text-slate-500 mt-0.5 truncate max-w-[200px]">
							{row.title}
						</p>
					)}
				</div>
			),
		},
		{
			key: "client_name",
			label: "Cliente",
			render: (val) => <span className="font-medium">{val}</span>,
		},
		{
			key: "company_name",
			label: "Empresa",
			render: (val) => <span className="text-slate-400">{val}</span>,
		},
		{
			key: "status",
			label: "Status",
			render: (val) => <StatusBadge status={val} />,
		},
		{
			key: "total_value",
			label: "Total",
			render: (val) => (
				<span className="text-green-400 font-bold">{formatCurrency(val)}</span>
			),
		},
		{
			key: "created_at",
			label: "Data",
			render: (val) => (
				<span className="text-slate-500 text-xs">{formatDate(val)}</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Propostas</h1>
				{canCreate() && (
					<Button onClick={() => navigate("/admin/proposals/new")}>
						<Plus className="w-4 h-4 inline mr-2" />
						Nova Proposta
					</Button>
				)}
			</div>

			{/* Filters */}
			<div className="bg-slate-800/50 backdrop-blur border border-white/5 rounded-xl p-4">
				<div className="flex flex-col md:flex-row gap-4">
					{/* Search */}
					<div className="flex-1">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
							<input
								type="text"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Buscar por numero, cliente, empresa ou titulo..."
								className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/5 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all"
							/>
						</div>
					</div>

					{/* Status Filter */}
					<div className="flex items-center gap-2">
						<Filter className="w-4 h-4 text-slate-400 hidden md:block" />
						<select
							value={statusFilter}
							onChange={(e) => handleStatusFilterChange(e.target.value)}
							className="px-3 py-2.5 bg-slate-900/50 border border-white/5 rounded-lg text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all appearance-none cursor-pointer min-w-[160px]"
						>
							{STATUS_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					{/* Search Button */}
					<button
						type="button"
						onClick={handleSearch}
						className="px-4 py-2.5 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/30 rounded-lg text-sm font-medium transition-all active:scale-95 flex items-center gap-2"
					>
						<Search className="w-4 h-4" />
						Buscar
					</button>
				</div>

				{/* Tabs */}
				<div className="mt-4">
					<Tabs tabs={tabs} active={activeTab} onChange={handleTabChange} />
				</div>
			</div>

			{/* Table */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl overflow-hidden">
				<div className="p-6">
					<Table
						columns={columns}
						data={proposals}
						loading={loading}
						actions={(row) => (
							<>
								<button
									type="button"
									onClick={() => navigate(`/admin/proposals/${row.id}`)}
									className="px-3 py-1 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 rounded text-xs transition-all active:scale-95 flex items-center gap-1"
								>
									<Eye className="w-3 h-3" /> Ver
								</button>
								{getProposalLink(row) && (
									<CopyButton
										text={getProposalLink(row)}
										label="Link"
										size="sm"
									/>
								)}
							</>
						)}
					/>
				</div>

				{/* Pagination */}
				{total > 0 && (
					<div className="flex items-center justify-between px-6 py-4 border-t border-white/5">
						<span className="text-sm text-slate-400">
							Mostrando{" "}
							<span className="font-medium text-slate-200">{startItem}</span>
							{"-"}
							<span className="font-medium text-slate-200">{endItem}</span>
							{" de "}
							<span className="font-medium text-slate-200">{total}</span>
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
								className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-700 bg-slate-800/50 text-slate-300 transition-all hover:bg-slate-700 hover:border-cyan-500/30 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-800/50 disabled:hover:border-slate-700 disabled:hover:text-slate-300 active:scale-95"
							>
								<ChevronLeft className="w-4 h-4" />
								Anterior
							</button>
							<span className="px-3 py-1.5 text-sm text-slate-400">
								Pagina <span className="font-medium text-cyan-400">{page}</span>
								{" de "}
								{totalPages}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page >= totalPages}
								className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-700 bg-slate-800/50 text-slate-300 transition-all hover:bg-slate-700 hover:border-cyan-500/30 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-800/50 disabled:hover:border-slate-700 disabled:hover:text-slate-300 active:scale-95"
							>
								Proximo
								<ChevronRight className="w-4 h-4" />
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
